// https://stackoverflow.com/a/51432792
const dynamicImportSpecifierRe =
	/import[\s\r\n]*\([^'"]*(['"])(?<specifier>(?:\1|(?!\1).)*)\1[^)]*\)/;

// https://stackoverflow.com/a/15123777
const commentRe = /\/\*[\s\S]*?\*\/|([^\\:]|^)\/\/.*$/gm;

export type JSResource = {
	moduleId: string;
	source: string;
};

export type Transformation = {
	code: string;
	meta: {
		jsResources: JSResource[];
	};
};

export type Api = {
	imported: string;
	local: string;
	optimize: (opts: { code: string; loader: string }) => string;
};

type TransformUsage = {
	optimize: Api['optimize'];
	usage: string;
} & (
	| {
			getModule(request: string): Promise<{ moduleId: string; moduleName?: string }>;
	  }
	| {
			getModuleId(request: string): Promise<string>;
	  }
);

function stringify({ moduleId, moduleName }: { moduleId: string; moduleName?: string }) {
	const properties = [`moduleId: "${moduleId}"`];
	if (moduleName) {
		properties.push(`moduleName: "${moduleName}"`);
	}

	return `{ ${properties.join(', ')} }`;
}

async function transformUsage({
	optimize,
	usage,
	...getModuleOrModuleId
}: TransformUsage): Promise<[string, JSResource]> {
	// Strip comments, then find the first import
	const usageWithoutComments = usage.replace(commentRe, '');
	const source = usageWithoutComments.match(dynamicImportSpecifierRe)?.groups?.specifier;

	if (!source) {
		throw new Error(`Unable to determine source for usage: ${usage}`);
	}

	const args = [''];
	const end = usage.lastIndexOf(')');
	for (let i = usage.indexOf('(') + 1, argi = 0, blocks = []; i < end; i++) {
		const char = usage[i];

		args[argi] += char;

		if (char === ',' && (!blocks.length || (blocks.length === 1 && blocks[0] === '=>'))) {
			argi += 1;
			args[argi] = '';
			blocks.length = 0;
			continue;
		}

		const block = ['{', '(', '=>'].find(
			(c) => c === (c.length === 1 ? char : usage.substring(i - c.length, i)),
		);

		if (block) {
			blocks.push(block);
		} else {
			const endBlock = ['}', ')'].find((c) => c === char);
			if (endBlock) {
				const startBlock = endBlock === '}' ? '{' : '(';
				blocks.splice(blocks.lastIndexOf(startBlock));
			}
		}
	}

	const [loaderWithTrailingComma, opts = ''] = [args[0].trim(), args[1]];
	const loader = loaderWithTrailingComma.replace(/,$/, '');
	// @ts-expect-error This is an intersection type TS cannot understand that will be removed soon
	const module = getModuleOrModuleId.getModule
		? // @ts-expect-error This is an intersection type TS cannot understand that will be removed soon
			await getModuleOrModuleId.getModule(source)
		: // @ts-expect-error This is an intersection type TS cannot understand that will be removed soon
			{ moduleId: await getModuleOrModuleId.getModuleId(source) };

	const options = stringify(module);

	const code = opts.includes('{')
		? usage.replace(
				opts,
				' ' + options.slice(0, -1).trim() + ', ' + opts.replace('{', '').trimStart(),
			)
		: usage.replace(loaderWithTrailingComma, `${loader}, ${options}`);

	return [
		optimize({
			code,
			loader,
		}),
		{
			...module,
			source,
		},
	];
}

export type TransformUsages = {
	apis: Api[];
	source: string;
} & (
	| {
			getModule(request: string): Promise<{ moduleId: string; moduleName?: string }>;
	  }
	| {
			getModuleId(request: string): Promise<string>;
	  }
);

export async function transformUsages({
	apis,
	source,
	...opts
}: TransformUsages): Promise<Transformation> {
	let code = source;
	const jsResources: JSResource[] = [];
	for (const { local, optimize } of apis) {
		// This is going to assume you call the library, with an optional generic type parameter, and no whitespace...
		const usageStartRe = new RegExp(`${local}(?:<[^>]+[>\\s]+)?\\(`);

		let usageIndex = 0;
		let usageStart;
		while ((usageStart = code.substring(usageIndex).match(usageStartRe))) {
			let usage = usageStart[0];
			usageIndex = code.indexOf(usage, usageIndex);
			const parenthesis = ['('];
			for (
				let i = usageIndex + usageStart[0].length;
				parenthesis.length ? i < code.length : false;
				i++
			) {
				if (code[i] === '(') {
					parenthesis.push('(');
				} else if (code[i] === ')') {
					parenthesis.pop();
				}

				usage += code[i];
			}

			const [transformedUsage, jsResource] = await transformUsage({
				optimize,
				usage,
				...opts,
			});

			code = code.replace(usage, transformedUsage);
			usageIndex += transformedUsage.length;
			jsResources.push(jsResource);
		}
	}

	return {
		code,
		meta: {
			jsResources,
		},
	};
}
